/**
 * How an HTML file record presents on a canvas.
 *
 * The file record is the source of truth (`metadata.display`); the canvas
 * node carries a synced copy so the card can be routed synchronously from
 * room storage — paint never waits on record hydration. This module is the
 * ONE place that reads the node copy, in both shapes it has ever had:
 *
 *   data.display      — the current model: `{mode, chromeless, layer}`
 *   data.presentation — the legacy marker `{frameless, transparent, layer,
 *                       interactive}`, whose presence meant "block"
 *
 * The legacy read is permanent: existing canvases and published snapshots
 * carry the old shape and are never rewritten. Keeping the mapping here means
 * retiring it — if ever — is a one-function change.
 */

export type BlockDisplayMode = "block" | "document";
export type BlockLayer = "front" | "back";

export interface BlockDisplay {
  mode: BlockDisplayMode;
  /** No card chrome and no background — the HTML composites directly onto
   *  the canvas ground. Blocks only. */
  chromeless: boolean;
  /** Stacking intent, a per-placement fact. Consumed by the backend at
   *  placement time (node z-index); carried here for completeness. */
  layer: BlockLayer;
}

/** The legacy node marker, kept only as the normalizer's input type. */
export interface LegacyBlockPresentation {
  frameless?: boolean;
  transparent?: boolean;
  layer?: BlockLayer;
  interactive?: boolean;
}

let legacyReads = 0;
let legacyReadLogged = false;

/** How many times the legacy `data.presentation` branch has fired in this
 *  session — the signal for whether the mapping is still load-bearing. */
export function legacyBlockMarkerReadCount(): number {
  return legacyReads;
}

function noteLegacyRead(): void {
  legacyReads += 1;
  if (legacyReadLogged) return;
  legacyReadLogged = true;
  if (typeof console !== "undefined" && typeof console.info === "function") {
    console.info(
      "[cohesive] rendering a block from the legacy data.presentation marker",
    );
  }
}

function layerOf(value: unknown): BlockLayer {
  return value === "back" ? "back" : "front";
}

/**
 * Resolve a node's block display from its data, or null when the node is not
 * an HTML block (documents, images, everything else). Presence of either
 * marker with block mode is what routes a node to the block card.
 */
export function blockDisplayFromNodeData(data: unknown): BlockDisplay | null {
  if (!data || typeof data !== "object") return null;
  const record = data as {
    display?: unknown;
    presentation?: unknown;
  };

  const display = record.display;
  if (display && typeof display === "object") {
    const d = display as {
      mode?: unknown;
      chromeless?: unknown;
      layer?: unknown;
    };
    if (d.mode === "block" || d.mode === "document") {
      return {
        mode: d.mode,
        chromeless: d.mode === "block" && d.chromeless === true,
        layer: layerOf(d.layer),
      };
    }
  }

  // Legacy: the marker's PRESENCE meant block (an empty object is a framed
  // block with all defaults). `interactive` is dropped — interactivity is
  // detected empirically now.
  const presentation = record.presentation;
  if (presentation != null && typeof presentation === "object") {
    noteLegacyRead();
    const p = presentation as LegacyBlockPresentation;
    return {
      mode: "block",
      chromeless: p.frameless === true || p.transparent === true,
      layer: layerOf(p.layer),
    };
  }

  return null;
}

/** True when the node renders as a live block. */
export function isBlockNodeData(data: unknown): boolean {
  return blockDisplayFromNodeData(data)?.mode === "block";
}
